import React, { ChangeEvent, useState } from 'react';
import { Box, Grid, TextField, Typography, Button } from '@mui/material';
import assets from '../../../assets';
import { typo } from '../../../assets/fonts/font';
import colorConfigs from '../../../configs/colorConfigs';
import smartFridgeStyles from '../../../styles/PublicPage/smartFridge';
import { ContactFormData } from '../../../models/IContactForm';
import { submitContactForm } from '../../../api/ContactFormAPI';
import { toast } from '../SnackBar/snackBar';

interface ContactFormProps {
  initialRemarks?: string;
}

const ContactForm = ({ initialRemarks = '' }: ContactFormProps) => {
  const [emailHelperText, setEmailHelperText] = useState('');
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    mobile_no: '',
    email: '',
    message: '',
    status: 'pending', // Default status
    remarks: initialRemarks,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setError] = useState<string | null>(null);

  const validateEmail = (email:string) => {
    // Basic email regex pattern for validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      email: emailValue,
    }));
  
    const result = validateEmail(emailValue);
    const text = result ? '' : 'Please enter a valid email address';
    setEmailHelperText(text);  // Show helper text when invalid email
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
  
    if (!formData.name || !formData.mobile_no || !formData.email || !formData.message) {
      setError("All fields are required.");
      setIsSubmitting(false);
      return;
    }
  
    // Validate email format
    if (!validateEmail(formData.email)) {
      setError("Please enter a valid email address.");
      setIsSubmitting(false);
      return;
    }

    try {
      await submitContactForm(formData);
      toast.success("Contact Form sent successfully!");
      // Clear all the form fields after successful submission
      setFormData({
        name: '',
        mobile_no: '',
        email: '',
        message: '',
        status: 'pending', // Default status
        remarks: initialRemarks,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error("Failed to send Contact Form. Please try again later!");
    } finally {
      setIsSubmitting(false);
    }
  };
  

  return (
    <Box
        component="form"
        sx={{
          position: 'relative',
          mt: 4,
          maxWidth: '800px',
          width: '75%',
          mx: 'auto',
          p: 3,
          border: '2px solid',
          borderRadius: {xs:'20px', sm:'80px'},
          boxShadow: 3,
          backgroundColor: colorConfigs.secondary,
        }}
        onSubmit={handleSubmit}
      >
        <Typography
          variant="h5"
          sx={{
            ...typo.sectionHeadline,
            mb: 3,
            textAlign: 'center',
            fontWeight: 'bold',
          }}
        >
          Contact Us
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              variant="outlined"
              fullWidth
              required
              name="name"
              value={formData.name}
              onChange={handleChange}  
              InputProps={{ sx: { input: { ...typo.bodyContent, backgroundColor:colorConfigs.quaternary
              }, }, }}
              InputLabelProps={{ sx: { ...typo.bodyContent, }, }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    ...typo.bodyContent,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colorConfigs.primary,
                    ...typo.bodyContent,
                  },
                },
                "& .MuiInputLabel-root": {},
                "& .MuiInputLabel-root.Mui-focused": {
                  ...typo.bodyContent,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Mobile No"
              variant="outlined"
              fullWidth
              required
              name="mobile_no"
              value={formData.mobile_no}
              onChange={handleChange}  
              InputProps={{ sx: { input: { ...typo.bodyContent,backgroundColor:colorConfigs.quaternary}, }, }}
              InputLabelProps={{ sx: { ...typo.bodyContent, }, }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    ...typo.bodyContent,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colorConfigs.primary,
                    ...typo.bodyContent,
                  },
                },
                "& .MuiInputLabel-root": {},
                "& .MuiInputLabel-root.Mui-focused": {
                  ...typo.bodyContent,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              variant="outlined"
              fullWidth
              required
              name="email"
              value={formData.email}
              onChange={handleEmailChange}  
              InputProps={{ sx: { input: { ...typo.bodyContent,backgroundColor:colorConfigs.quaternary}, }, }}
              InputLabelProps={{ sx: { ...typo.bodyContent, }, }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    ...typo.bodyContent,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colorConfigs.primary,
                    ...typo.bodyContent,
                  },
                },
                "& .MuiInputLabel-root": {},
                "& .MuiInputLabel-root.Mui-focused": {
                  ...typo.bodyContent,
                },
              }}
              helperText={emailHelperText}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Remarks"
              variant="outlined"
              fullWidth
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}  
              InputProps={{ sx: { input: { ...typo.bodyContent,backgroundColor:colorConfigs.quaternary}, }, }}
              InputLabelProps={{ sx: { ...typo.bodyContent, }, }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    ...typo.bodyContent,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colorConfigs.primary,
                    ...typo.bodyContent,
                  },
                },
                "& .MuiInputLabel-root": {},
                "& .MuiInputLabel-root.Mui-focused": {
                  ...typo.bodyContent,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
          <TextField
            label="Message"
            variant="outlined"
            fullWidth
            required
            name="message"
            value={formData.message}
            onChange={handleChange}
            InputProps={{ 
              multiline:true,
              rows:4,
              inputComponent:'textarea',
              sx: { input: { ...typo.bodyContent }, }, }}
            InputLabelProps={{ sx: { ...typo.bodyContent, }, }}
            sx={{
              "& .MuiOutlinedInput-root": {
                ...typo.bodyContent,"& fieldset": {...typo.bodyContent},
                "&.Mui-focused fieldset": {borderColor: colorConfigs.primary},
                "& .MuiOutlinedInput-input": {...typo.bodyContent},
              },
              "& .MuiInputLabel-root": {...typo.bodyContent},
              "& .MuiInputLabel-root.Mui-focused": {...typo.bodyContent},
              backgroundColor: colorConfigs.quaternary
            }}
          />
            
          </Grid>
          <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                sx={{ ...typo.button, ...smartFridgeStyles.submitButton }}
                disabled={isSubmitting} 
              >
              Submit
            </Button>
          </Grid>
        </Grid>
        <Box
          component="img"
          src={assets.images.popupBorder}
          alt="Cartoon Character"
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translate(calc(75% + 6px), -50%)',
            maxWidth: '100px',
            width: "18%",
            height: 'auto'
          }}
        />
      </Box>
  );
};

export default ContactForm;
